import { type FC, memo, useMemo } from 'react';
import type { GameV1 } from '@/api/games';
import type { TCardMode } from '@/modules/game/types';
import { useGetCommonStore } from '@/store';
import { EmptyGameCard } from './EmptyGameCard';
import { GameCard } from './GameCard';
import { useGameDemoAction } from './hooks/useGameDemoAction';
import { useGamePlayAction } from './hooks/useGamePlayAction';
import { LockedGameCard } from './LockedGameCard';
import { SmallGameCard } from './SmallGameCard';
import { SmallLockedGameCard } from './SmallLockedGameCard';

export interface IGameCardFactoryProps {
  game?: GameV1;
  isRtpShown?: boolean;
  isEmpty?: boolean;
  isEveryEmpty?: boolean;
  isSmall?: boolean;
  withoutBottom?: boolean;
  mode?: TCardMode;
  onPlayClick?: (game: GameV1) => void;
  onDemoClick?: (game: GameV1) => void;
}

export const GameCardFactory: FC<IGameCardFactoryProps> = memo(
  ({
    game,
    isEmpty,
    isSmall,
    mode = 'dark',
    withoutBottom = false,
    isEveryEmpty = false,
    onPlayClick,
    onDemoClick,
  }) => {
    const useCommonStore = useGetCommonStore();

    const user = useCommonStore.use.user();

    const handlePlayClick = useGamePlayAction(game, onPlayClick);
    const handleDemoClick = useGameDemoAction(game, onDemoClick);

    const isLocked = useMemo(() => {
      if (!(user && game)) return false;
      return !game.currencies[user.currency];
    }, [user, game]);

    const Card = useMemo(() => {
      switch (true) {
        case !!game && isLocked && isSmall:
          return <SmallLockedGameCard game={game} />;
        case !!game && isLocked:
          return (
            <LockedGameCard
              game={game}
              mode={mode}
              withoutBottom={withoutBottom}
            />
          );
        case isEmpty:
          return (
            <EmptyGameCard
              isEveryEmpty={isEveryEmpty}
              mode={mode}
              withoutBottom={withoutBottom}
            />
          );
        case !!game && isSmall:
          return <SmallGameCard game={game} />;
        case !!game:
          return (
            <GameCard
              game={game}
              mode={mode}
              onDemoClick={handleDemoClick}
              onPlayClick={handlePlayClick}
              withoutBottom={withoutBottom}
            />
          );
        default:
          return null;
      }
    }, [
      game,
      isLocked,
      isSmall,
      mode,
      withoutBottom,
      isEmpty,
      handlePlayClick,
      handleDemoClick,
    ]);

    return Card;
  }
);
